(() => {
  const root = document.documentElement;
  const body = document.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  root.classList.add('motion-ready');

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch {
      return `£${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 30);
  };

  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header || header.dataset.initialized) return;
    header.dataset.initialized = 'true';
    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => { updateHeader(); ticking = false; });
    }, { passive: true });
    updateHeader();

    document.querySelectorAll('.navigation-dropdown').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('.navigation-dropdown[open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      });
    });
  };

  const initMobileMenu = () => {
    const menuButton = document.querySelector('[data-menu-open]');
    const menuCloseButton = document.querySelector('[data-menu-close]');
    const menuBackdrop = document.querySelector('[data-menu-backdrop]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!menu || menu.dataset.initialized) return;
    menu.dataset.initialized = 'true';
    let returnFocus = null;

    const setOpen = (open) => {
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      menu.inert = !open;
      if (menuBackdrop) menuBackdrop.hidden = !open;
      menuButton?.setAttribute('aria-expanded', String(open));
      body.classList.toggle('menu-open', open);
      if (open) {
        returnFocus = document.activeElement;
        window.requestAnimationFrame(() => menuCloseButton?.focus());
      } else if (returnFocus instanceof HTMLElement) {
        returnFocus.focus();
        returnFocus = null;
      }
    };

    menuButton?.addEventListener('click', () => setOpen(true));
    menuCloseButton?.addEventListener('click', () => setOpen(false));
    menuBackdrop?.addEventListener('click', () => setOpen(false));
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
    });
  };

  const initSearch = () => {
    const dialog = document.querySelector('[data-search-dialog]');
    if (!dialog || dialog.dataset.initialized) return;
    dialog.dataset.initialized = 'true';
    const input = dialog.querySelector('[data-predictive-search-input]');
    const results = dialog.querySelector('[data-predictive-search-results]');
    let debounceTimer;
    let controller;

    const open = () => {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      window.requestAnimationFrame(() => input?.focus());
    };
    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };

    document.querySelectorAll('[data-search-open]').forEach((button) => button.addEventListener('click', open));
    dialog.querySelector('[data-search-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });

    const renderResults = (payload) => {
      if (!results) return;
      results.replaceChildren();
      const resources = payload?.resources?.results || {};
      const items = [...(resources.products || []), ...(resources.articles || []), ...(resources.pages || [])].slice(0, 6);
      if (!items.length) {
        const empty = document.createElement('p');
        empty.textContent = 'No matching products or guides yet.';
        results.append(empty);
        return;
      }
      const list = document.createElement('ul');
      list.setAttribute('role', 'list');
      items.forEach((item) => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = item.title;
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.width = 70;
          image.height = 70;
          link.prepend(image);
        }
        li.append(link);
        list.append(li);
      });
      results.append(list);
    };

    input?.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) { results?.replaceChildren(); return; }
      debounceTimer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const endpoint = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=6`;
          const response = await fetch(endpoint, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Search unavailable');
          renderResults(await response.json());
        } catch (error) {
          if (error.name !== 'AbortError' && results) results.textContent = 'Search suggestions are temporarily unavailable.';
        }
      }, 220);
    });
  };

  const initNewsletterPopup = () => {
    const popup = document.querySelector('[data-newsletter-popup]');
    if (!popup || popup.dataset.initialized) return;
    popup.dataset.initialized = 'true';
    const storageKey = 'unlimited-straps-newsletter-dismissed';
    let dismissed = false;
    try { dismissed = window.sessionStorage.getItem(storageKey) === 'true'; } catch { dismissed = false; }

    const close = () => {
      try { window.sessionStorage.setItem(storageKey, 'true'); } catch { /* session storage is optional */ }
      if (typeof popup.close === 'function') popup.close(); else popup.removeAttribute('open');
    };
    popup.querySelectorAll('[data-newsletter-popup-close]').forEach((button) => button.addEventListener('click', close));
    popup.addEventListener('click', (event) => { if (event.target === popup) close(); });
    if (dismissed || window.Shopify?.designMode) return;

    const tryOpen = () => {
      if (popup.open) return;
      if (document.querySelector('[data-search-dialog][open]') || document.querySelector('[data-mobile-menu].is-open')) {
        window.setTimeout(tryOpen, 2500);
        return;
      }
      if (typeof popup.showModal === 'function') popup.showModal(); else popup.setAttribute('open', '');
      window.requestAnimationFrame(() => popup.querySelector('input')?.focus());
    };
    window.setTimeout(tryOpen, Number(popup.dataset.delay) || 8000);
  };

  const initMotion = (scope = document) => {
    const revealElements = [...scope.querySelectorAll('.reveal:not([data-reveal-ready])')];
    revealElements.forEach((element) => { element.dataset.revealReady = 'true'; });
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, activeObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        activeObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealElements.forEach((element) => observer.observe(element));
  };

  const initHero = (scope = document) => {
    scope.querySelectorAll('[data-hero-slides]:not([data-initialized])').forEach((container) => {
      container.dataset.initialized = 'true';
      const slides = [...container.querySelectorAll('.hero-slide')];
      if (reduceMotion || slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      window.setInterval(() => {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, Number(container.dataset.interval) || 8000);
    });
  };

  const initProductTabs = (scope = document) => {
    scope.querySelectorAll('.product-tabs:not([data-initialized])').forEach((tablist) => {
      tablist.dataset.initialized = 'true';
      const tabs = [...tablist.querySelectorAll('[data-product-tab]')];
      const panels = tabs.map((tab) => document.querySelector(`[data-product-panel="${CSS.escape(tab.dataset.productTab)}"]`));
      const activate = (selectedTab) => {
        tabs.forEach((tab, index) => {
          const selected = tab === selectedTab;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (panels[index]) panels[index].hidden = !selected;
        });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === index) return;
          event.preventDefault();
          tabs[next].focus();
          activate(tabs[next]);
        });
      });
    });
  };

  const initRotatingBenefits = (scope = document) => {
    scope.querySelectorAll('[data-rotating-benefit]:not([data-initialized])').forEach((target) => {
      target.dataset.initialized = 'true';
      if (reduceMotion) return;
      const values = [...target.parentElement.querySelectorAll('[data-benefit-values] span')].map((item) => item.textContent.trim()).filter(Boolean);
      if (values.length < 2) return;
      let index = 0;
      window.setInterval(() => {
        target.classList.add('is-changing');
        window.setTimeout(() => {
          index = (index + 1) % values.length;
          target.textContent = values[index];
          target.classList.remove('is-changing');
        }, 240);
      }, 3000);
    });
  };

  const initUncleGallery = (scope = document) => {
    scope.querySelectorAll('[data-uncle-flick]:not([data-initialized])').forEach((container) => {
      container.dataset.initialized = 'true';
      const frames = [...container.querySelectorAll('.uncle-frame')];
      if (!frames.length) return;
      frames[0].classList.add('is-active');
      let timer;
      let index = 0;
      let changes = 0;
      const show = (selected) => frames.forEach((frame, current) => frame.classList.toggle('is-active', current === selected));
      const stop = (reset = true) => {
        window.clearInterval(timer);
        timer = null;
        if (reset) { index = 0; show(index); }
      };
      const start = () => {
        if (frames.length < 2 || timer) return;
        if (reduceMotion) { show(1); return; }
        changes = 0;
        timer = window.setInterval(() => {
          index = (index + 1) % frames.length;
          show(index);
          changes += 1;
          if (changes >= frames.length * 2 - 1) stop(false);
        }, 170);
      };
      container.addEventListener('pointerenter', start);
      container.addEventListener('pointerleave', () => stop(true));
      container.addEventListener('focusin', start);
      container.addEventListener('focusout', () => stop(true));
    });
  };

  const initGallery = (scope = document) => {
    scope.querySelectorAll('[data-product-gallery]:not([data-initialized])').forEach((gallery) => {
      gallery.dataset.initialized = 'true';
      const media = [...gallery.querySelectorAll('[data-media-id]')];
      const buttons = [...gallery.querySelectorAll('[data-media-target]')];
      buttons.forEach((button) => button.addEventListener('click', () => {
        const target = button.dataset.mediaTarget;
        media.forEach((item) => { item.hidden = item.dataset.mediaId !== target; item.classList.toggle('is-active', item.dataset.mediaId === target); });
        buttons.forEach((item) => { const selected = item === button; item.classList.toggle('is-active', selected); item.setAttribute('aria-pressed', String(selected)); });
      }));
    });
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
      document.querySelectorAll('[data-cart-link]').forEach((link) => {
        link.setAttribute('aria-label', `Basket, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
      });
    } catch { /* cart page remains available */ }
  };

  const initProductForms = (scope = document) => {
    scope.querySelectorAll('form[action*="/cart/add"]:not([data-initialized])').forEach((form) => {
      form.dataset.initialized = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        const message = form.querySelector('[data-product-form-message]');
        button?.setAttribute('aria-busy', 'true');
        if (button) button.disabled = true;
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || 'This item could not be added.');
          if (message) { message.textContent = 'Added to your basket.'; message.classList.remove('is-error'); }
          announce(`${payload.product_title || 'Item'} added to your basket.`);
          await updateCartCount();
        } catch (error) {
          if (message) { message.textContent = error.message; message.classList.add('is-error'); }
          announce(error.message);
        } finally {
          button?.removeAttribute('aria-busy');
          if (button) button.disabled = false;
        }
      });
    });
  };

  const initProductVariants = (scope = document) => {
    scope.querySelectorAll('[data-product-section]:not([data-variant-initialized])').forEach((section) => {
      section.dataset.variantInitialized = 'true';
      const variantData = section.querySelector('[data-product-variants]');
      const form = section.querySelector('[data-product-form]');
      if (!variantData || !form) return;
      const variants = JSON.parse(variantData.textContent);
      const selects = [...form.querySelectorAll('[data-option-position]')];
      const idInput = form.querySelector('[data-variant-id]');
      const priceRegion = section.querySelector('[data-product-price]');
      const availability = section.querySelector('[data-product-availability]');
      const button = form.querySelector('[data-add-to-cart]');
      const buttonText = form.querySelector('[data-add-to-cart-text]');

      const update = () => {
        const selectedOptions = selects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));
        if (!variant) {
          if (button) button.disabled = true;
          if (buttonText) buttonText.textContent = 'Unavailable';
          if (availability) availability.textContent = 'This combination is unavailable.';
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceRegion) {
          priceRegion.replaceChildren();
          const wrapper = document.createElement('div');
          wrapper.className = 'price';
          const current = document.createElement('span');
          current.textContent = money(variant.price);
          wrapper.append(current);
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            wrapper.classList.add('price--on-sale');
            const old = document.createElement('s');
            old.textContent = money(variant.compare_at_price);
            wrapper.append(old);
          }
          priceRegion.append(wrapper);
        }
        if (button) button.disabled = !variant.available;
        if (buttonText) buttonText.textContent = variant.available ? 'Add to basket' : 'Sold out';
        if (availability) availability.textContent = variant.available ? 'In stock and ready to order' : 'Currently unavailable';
        if (variant.featured_media?.id) section.querySelector(`[data-media-target="${variant.featured_media.id}"]`)?.click();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      selects.forEach((select) => select.addEventListener('change', update));
    });
  };

  const initQuantities = (scope = document) => {
    scope.querySelectorAll('[data-quantity-input]:not([data-initialized])').forEach((wrapper) => {
      wrapper.dataset.initialized = 'true';
      const input = wrapper.querySelector('input');
      const adjust = (direction) => {
        if (!input) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        input.value = Math.max(min, Number(input.value || min) + direction * step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => adjust(-1));
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => adjust(1));
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('product-recommendations[data-url]:not([data-initialized])').forEach(async (container) => {
      container.dataset.initialized = 'true';
      try {
        const response = await fetch(container.dataset.url);
        if (!response.ok) return;
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const replacement = html.querySelector('product-recommendations');
        if (replacement?.innerHTML.trim()) {
          container.innerHTML = replacement.innerHTML;
          initMotion(container);
          initProductForms(container);
        }
      } catch { /* recommendations are progressive enhancement */ }
    });
  };

  const initSort = (scope = document) => {
    scope.querySelectorAll('[data-sort-select]:not([data-initialized])').forEach((select) => {
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  };

  const init = (scope = document) => {
    initHeader();
    initMobileMenu();
    initSearch();
    initNewsletterPopup();
    initMotion(scope);
    initHero(scope);
    initProductTabs(scope);
    initRotatingBenefits(scope);
    initUncleGallery(scope);
    initGallery(scope);
    initProductForms(scope);
    initProductVariants(scope);
    initQuantities(scope);
    initRecommendations(scope);
    initSort(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
